import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { calculateInvoiceTotals } from '../utils/helpers';
import Alert from '../components/Alert';
import InvoiceItemsTable from '../components/InvoiceItemsTable';
import Layout from '../components/Layout';
import CustomDropdown from '../components/CustomDropdown';
import { 
  Pencil, 
  ArrowLeft, 
  Save, 
  ShieldAlert, 
  Info, 
  Calculator, 
  CreditCard, 
  FileText,
  Loader2,

  FileSearch } from 'lucide-react';

const EditInvoice = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [products, setProducts] = useState([]);
    const [invoice, setInvoice] = useState(null);

    const [formData, setFormData] = useState({
        items: [],
        discount: 0,
        discountType: 'amount',
        notes: '',
        termsAndConditions: '',
        taxType: 'excluded',
        paymentDetails: {
            method: 'cash',
            dueDate: ''
        }
    });

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [invoiceRes, settingsRes, productsRes] = await Promise.all([
                api.get(`/invoices/${id}`),
                api.get('/settings'),
                api.get('/products?limit=1000')
            ]);

            const invoiceData = invoiceRes.data.invoice;
            setInvoice(invoiceData);
            setProducts(productsRes.data.products);

            // Check if invoice can be edited - canEdit is a boolean property in the API response
            if (invoiceData.isLocked) {
                setError('This invoice is locked and cannot be edited');
            } else if (invoiceData.status === 'cancelled') {
                setError('Cancelled invoices cannot be edited');
            }

            // Map items and try to match with products
            const mappedItems = invoiceData.items.map(item => {
                // Try to find matching product by description or hsnSac
                const matchingProduct = productsRes.data.products.find(
                    p => p.description === item.description || p.hsnSac === item.hsnSac
                );

                return {
                    ...item,
                    product: matchingProduct ? matchingProduct._id : ''
                };
            });

            setFormData({
                items: mappedItems,
                discount: invoiceData.discount || 0,
                discountType: invoiceData.discountType || 'amount',
                taxType: invoiceData.taxType || 'excluded',
                notes: invoiceData.notes || '',
                termsAndConditions: invoiceData.termsAndConditions || '',
                paymentDetails: {
                    method: invoiceData.paymentDetails?.method || 'cash',
                    dueDate: invoiceData.paymentDetails?.dueDate ? new Date(invoiceData.paymentDetails.dueDate).toISOString().split('T')[0] : '',
                    chequeNumber: invoiceData.paymentDetails?.chequeNumber || ''
                }
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load invoice');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('paymentDetails.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                paymentDetails: {
                    ...prev.paymentDetails,
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validation
        if (formData.items.length === 0) {
            setError('At least one item is required');
            return;
        }

        const hasEmptyItems = formData.items.some(
            item => (!item.productName && !item.description) || !item.hsnSac || Number(item.quantity) <= 0 || Number(item.rate) < 0
        );

        if (hasEmptyItems) {
            setError('Please fill all item details (product name/description, HSN/SAC, quantity, and rate)');
            return;
        }

        try {
            setLoading(true);
            
            // Sanitize items array to remove empty product fields and ensure description is filled
            const sanitizedData = {
                ...formData,
                items: formData.items.map(item => {
                    const sanitizedItem = { ...item };
                    sanitizedItem.description = (sanitizedItem.description || sanitizedItem.productName || '').trim();
                    sanitizedItem.productName = (sanitizedItem.productName || '').trim();
                    sanitizedItem.unit = (sanitizedItem.unit || 'pcs').trim();
                    if (!sanitizedItem.product) {
                        delete sanitizedItem.product;
                    }
                    return sanitizedItem;
                })
            };

            await api.put(`/invoices/${id}`, sanitizedData);
            setSuccess('Invoice updated successfully!');

            setTimeout(() => {
                navigate(`/invoice/${id}`);
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update invoice');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div style={{ padding: '40px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                    <Loader2 size={40} className="animate-spin" style={{ color: '#3b82f6' }} />
                    <p>Loading Invoice...</p>
                </div>
            </Layout>
        );
    }

    if (!invoice) {
        return (
            <Layout>
                <div className="page-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FileSearch size={28} style={{ color: '#ef4444' }} />
                        <h1>Invoice Not Found</h1>
                    </div>
                    <button className="btn btn-secondary" onClick={() => navigate('/invoice')} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ArrowLeft size={18} /> Back to Invoices
                    </button>
                </div>
            </Layout>
        );
    }

    const isIGST = invoice.isIGST;
    const totals = calculateInvoiceTotals(formData.items, isIGST, formData.discount, formData.discountType, formData.taxType);

    const canEdit = !invoice.isLocked && invoice.status === 'active';

    return (
        <div>
            <div>
                <div className="page-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Pencil size={28} style={{ color: '#3b82f6' }} />
                        <h1>Edit Invoice - {invoice.invoiceNumber}</h1>
                    </div>
                    <button className="btn btn-secondary" onClick={() => navigate(`/invoice/${id}`)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ArrowLeft size={18} /> Back to Invoice
                    </button>
                </div>

                {error && <Alert type="error" message={error} onClose={() => setError('')} />}
                {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

                {!canEdit ? (
                    <div className="card">
                        <div style={{ padding: '40px', textAlign: 'center', color: '#e74c3c', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                            <ShieldAlert size={48} />
                            <div>
                                <h3 style={{ margin: '0 0 10px 0' }}>This invoice cannot be edited</h3>
                                <p style={{ margin: 0 }}>{invoice.isLocked ? 'The invoice is locked' : 'The invoice has been cancelled'}</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                         <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 10 }}>
                            <div className="card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Info size={18} /> Invoice Details (Read-Only)
                                </div>
                            </div>
                            <div style={{ padding: '15px', borderRadius: '5px' }}>
                                <div><strong>Invoice Number:</strong> {invoice.invoiceNumber}</div>
                                <div><strong>Invoice Date:</strong> {new Date(invoice.invoiceDate).toLocaleDateString()}</div>
                                <div><strong>Customer:</strong> {invoice.customerDetails.name} - {invoice.customerDetails.phone}</div>
                                <div><strong>Place of Supply:</strong> {invoice.customerDetails.placeOfSupply}</div>
                                <div><strong>Tax Configuration:</strong> {isIGST ? 'IGST' : 'CGST + SGST'}</div>
                                
                                <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                                    <label className="form-label" style={{ fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                                        Tax Calculation Mode
                                    </label>
                                    <select
                                        name="taxType"
                                        value={formData.taxType}
                                        onChange={(e) => setFormData(prev => ({ ...prev, taxType: e.target.value }))}
                                        className="form-control"
                                        style={{ maxWidth: '250px' }}
                                    >
                                        <option value="excluded">Tax Excluded</option>
                                        <option value="included">Tax Included</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 9 }}>
                            <InvoiceItemsTable
                                items={formData.items}
                                onItemsChange={(items) => setFormData(prev => ({ ...prev, items }))}
                                products={products}
                                originalItems={invoice.items}
                            />
                        </div>

                        <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 8 }}>
                            <div className="card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Calculator size={18} /> Totals
                                </div>
                            </div>
                            <table style={{ width: '100%', maxWidth: '400px', marginLeft: 'auto' }}>
                                <tbody>
                                    <tr>
                                        <td><strong>Subtotal:</strong></td>
                                        <td style={{ textAlign: 'right' }}>₹{totals.subtotal.toFixed(2)}</td>
                                    </tr>
                                    {formData.discount > 0 && (
                                        <tr>
                                            <td><strong>Discount ({formData.discountType === 'percentage' ? `${formData.discount}%` : '₹'}):</strong></td>
                                            <td style={{ textAlign: 'right', color: '#e74c3c' }}>-₹{totals.discountAmount.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    {isIGST ? (
                                        <tr>
                                            <td><strong>IGST:</strong></td>
                                            <td style={{ textAlign: 'right' }}>₹{totals.igst.toFixed(2)}</td>
                                        </tr>
                                    ) : (
                                        <>
                                            <tr>
                                                <td><strong>CGST:</strong></td>
                                                <td style={{ textAlign: 'right' }}>₹{totals.cgst.toFixed(2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>SGST:</strong></td>
                                                <td style={{ textAlign: 'right' }}>₹{totals.sgst.toFixed(2)}</td>
                                            </tr>
                                        </>
                                    )}
                                    <tr>
                                        <td><strong>Total Tax:</strong></td>
                                        <td style={{ textAlign: 'right' }}>₹{totals.totalTax.toFixed(2)}</td>
                                    </tr>
                                    {totals.roundOff !== 0 && (
                                        <tr>
                                            <td><strong>Round Off:</strong></td>
                                            <td style={{ textAlign: 'right' }}>₹{totals.roundOff.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    <tr style={{ borderTop: '2px solid #333', fontSize: '18px' }}>
                                        <td><strong>Grand Total:</strong></td>
                                        <td style={{ textAlign: 'right' }}><strong>₹{totals.grandTotal.toFixed(2)}</strong></td>
                                    </tr>
                                </tbody>
                            </table>

                            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                                <h4>Apply Discount</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Discount Type</label>
                                        <CustomDropdown
                                            name="discountType"
                                            value={formData.discountType}
                                            onChange={handleChange}
                                            options={[
                                                { value: 'amount', label: '₹ Amount' },
                                                { value: 'percentage', label: '% Percent' }
                                            ]}
                                            className="form-control"
                                            style={{ height: '42px', minWidth: '130px' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Discount Value</label>
                                        <input
                                            type="number"
                                            name="discount"
                                            className="form-control"
                                            value={formData.discount}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.01"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 7 }}>
                            <div className="card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CreditCard size={18} /> Payment Details
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Payment Method</label>
                                    <select
                                        name="paymentDetails.method"
                                        className="form-control"
                                        value={formData.paymentDetails.method}
                                        onChange={handleChange}
                                    >
                                        <option value="cash">Cash</option>
                                        <option value="online">Online</option>
                                        <option value="cheque">Cheque</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Due Date (if not paid)</label>
                                    <input
                                        type="date"
                                        name="paymentDetails.dueDate"
                                        className="form-control"
                                        value={formData.paymentDetails.dueDate}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 6 }}>
                            <div className="card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FileText size={18} /> Additional Information
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Notes</label>
                                <textarea
                                    name="notes"
                                    className="form-control"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Any additional notes for this invoice"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Terms & Conditions</label>
                                <textarea
                                    name="termsAndConditions"
                                    className="form-control"
                                    value={formData.termsAndConditions}
                                    onChange={handleChange}
                                    rows="4"
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate(`/invoice/${id}`)}
                            >
                                Cancel
                            </button>
                             <button
                                 type="submit"
                                 className="btn btn-primary"
                                 disabled={loading}
                                 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                             >
                                 {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                 {loading ? 'Updating...' : 'Update Invoice'}
                             </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EditInvoice;
